import { ExpertRequestTransitionAction } from '@/__generated__/graphql';

import type { CloseInput, HoldInput, OpenTransitionModal } from '../../hooks/useErTransitions';
import { CloseRequestModal } from './CloseRequestModal';
import { PutOnHoldModal } from './PutOnHoldModal';
import { SimpleConfirmModal } from './SimpleConfirmModal';

export interface ErTransitionModalsProps {
  openModal: OpenTransitionModal;
  submitting?: boolean;
  error?: string | null;
  /** Open (non-terminal) consultations on the ER; blocks Close when > 0. */
  openConsultationCount?: number;
  onDismiss: () => void;
  onSubmitForSourcing: () => void;
  onResume: () => void;
  onReopen: () => void;
  onPutOnHold: (input: HoldInput) => void;
  onClose: (input: CloseInput) => void;
}

/**
 * Renders the confirm modal for the in-progress lifecycle transition (ENG-5290
 * T2). Driven entirely by useErTransitions: positive confirms (Submit/Resume/
 * Reopen) use SimpleConfirmModal; Hold and Close use their dedicated form
 * modals. Modals stay open on error so the inline message shows.
 */
export function ErTransitionModals({
  openModal,
  submitting,
  error,
  openConsultationCount,
  onDismiss,
  onSubmitForSourcing,
  onResume,
  onReopen,
  onPutOnHold,
  onClose,
}: ErTransitionModalsProps): JSX.Element {
  return (
    <>
      <SimpleConfirmModal
        open={openModal === ExpertRequestTransitionAction.SubmitForSourcing}
        title="Submit for sourcing"
        message="We'll notify project members that this request is Active and ready for sourcing."
        confirmLabel="Submit"
        submitting={submitting}
        error={error}
        onConfirm={onSubmitForSourcing}
        onCancel={onDismiss}
      />

      <SimpleConfirmModal
        open={openModal === ExpertRequestTransitionAction.Resume}
        title="Resume sourcing"
        message="Expert Sourcing will be resumed and the request returned to Active."
        confirmLabel="Resume"
        submitting={submitting}
        error={error}
        onConfirm={onResume}
        onCancel={onDismiss}
      />

      <SimpleConfirmModal
        open={openModal === ExpertRequestTransitionAction.Reopen}
        title="Reopen request"
        message="The request returns to Active and sourcing continues."
        confirmLabel="Reopen"
        submitting={submitting}
        error={error}
        onConfirm={onReopen}
        onCancel={onDismiss}
      />

      <PutOnHoldModal
        open={openModal === ExpertRequestTransitionAction.PutOnHold}
        onCancel={onDismiss}
        onConfirm={onPutOnHold}
        submitting={submitting}
        error={error}
      />

      <CloseRequestModal
        open={openModal === ExpertRequestTransitionAction.Close}
        onCancel={onDismiss}
        onConfirm={onClose}
        submitting={submitting}
        error={error}
        openConsultationCount={openConsultationCount}
      />
    </>
  );
}
